/**
 * FP3-047 / FP3-047B — Public Explore page for Frame Practice.
 *
 * Mounted at /explore, deliberately OUTSIDE the ProtectedRoute
 * wrapper. Renders without an auth check and never fetches any
 * protected business data. The only `/api/*` call that fires is
 * the global AuthProvider's bootstrap to `/auth/me`, which lets
 * the page swap the Sign in CTA to "Go to dashboard" for visitors
 * who already have a session. No real student or provider names
 * appear anywhere on this page — every preview card uses neutral
 * demo labels (Student A, Provider 1, Campus A, etc.).
 *
 * Audience: special-education directors, school administrators,
 * related-service providers (SLPs, OTs, PTs, behavior specialists,
 * school psychologists), special-ed teachers, and small/pilot
 * schools evaluating Frame Practice.
 *
 * FP3-047B layout changes:
 *   - Sticky subnav with section anchors.
 *   - "Choose your view" audience cards immediately under the hero.
 *   - Reports & Trends rebuilt as a major visual section: trend
 *     graph, status cards, fidelity sparkline, attention list,
 *     data-recency card, filter mock, and supporting copy.
 *   - "For Administrators" tour grid — 6 cards, each with its own
 *     UI mock.
 *   - "For Providers" tour grid — 7 cards, each with its own UI
 *     mock.
 *   - Sections 8 (Admin Student Overview) and 9 (Provider
 *     Workspace) from FP3-047 were folded into the new tour grids
 *     so the page stops repeating the same idea three times.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  ChartLineUp,
  Target,
  CalendarCheck,
  Users,
  Brain,
  Lock,
  FilePdf,
  ClipboardText,
  Notebook,
  Buildings,
  HandHeart,
  ArrowRight,
  CheckCircle,
  Sparkle,
  ChartBar,
  Clock,
  GraduationCap,
  House,
  Compass,
  ChartPieSlice,
  Stethoscope,
  Eye,
  Books,
  WarningCircle,
  ClockCountdown,
  Funnel,
} from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";

const CONTACT_EMAIL = "admin@frameed.com";

export default function Explore() {
  const { user } = useAuth();

  // Set the tab title + meta description per spec. Restoring on
  // unmount keeps the rest of the app's title behavior unchanged.
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Frame Practice | Explore";
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content") || "";
    if (meta) {
      meta.setAttribute(
        "content",
        "Explore Frame Practice, a provider workspace for IEP goal tracking, service fidelity, data collection, scheduling, reports, and admin oversight. Solo providers can start a pilot today; school/team access is invite-only."
      );
    }
    return () => {
      document.title = prevTitle;
      if (meta) meta.setAttribute("content", prevDesc);
    };
  }, []);

  const signInLabel = user ? "Go to dashboard" : "Sign in";
  const signInTo = user
    ? ["admin", "org_admin", "super_admin"].includes(user.permission_role || "")
      ? "/admin/dashboard"
      : "/"
    : "/login";

  return (
    <div data-testid="explore-page" className="min-h-screen bg-[#F7F5EF] body-font text-[#1F2937]">
      <PublicHeader signInLabel={signInLabel} signInTo={signInTo} />
      <SubNav />
      <Hero signInLabel={signInLabel} signInTo={signInTo} />
      <SectionChooseYourView />
      <SectionWhatWeOrganize />
      <SectionReportsTrends />
      <SectionForAdmins />
      <SectionForProviders />
      <SectionGoalsProgress />
      <SectionDataCollection />
      <SectionScheduleFidelity />
      <SectionTeamCoordination />
      <SectionAssessmentsDocuments />
      <SectionAiSupport />
      <SectionPrivacyResponsibleUse />
      <SectionPilotProgram />
      <SectionScreenshots />
      <PublicFooter />
    </div>
  );
}

// ── Reusable shells ──────────────────────────────────────────────

function PublicHeader({ signInLabel, signInTo }) {
  return (
    <header
      data-testid="explore-header"
      className="border-b border-[#EDEAE0] bg-[#F7F5EF]/90 backdrop-blur supports-[backdrop-filter]:bg-[#F7F5EF]/70 sticky top-0 z-40"
    >
      <div className="mx-auto max-w-6xl px-5 sm:px-8 h-14 sm:h-16 flex items-center justify-between">
        <Link to="/explore" data-testid="explore-header-brand" className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-[#1E3F33] flex items-center justify-center shadow-sm">
            <span className="text-white text-sm font-bold heading-font">F</span>
          </div>
          <div className="leading-tight">
            <div className="text-[15px] sm:text-[16px] font-bold text-[#1E3F33] heading-font tracking-tight">
              Frame<span className="text-[#8FA998] font-medium ml-0.5">Practice</span>
            </div>
            <div className="hidden sm:block text-[10px] uppercase tracking-[0.14em] text-[#8FA998]">
              Provider workspace
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-2 sm:gap-3">
          <Link
            to="/start-pilot"
            data-testid="explore-header-start-pilot"
            className="hidden sm:inline-flex text-[12.5px] text-[#1E3F33] font-semibold hover:text-[#173127] underline decoration-[#8FA998]/60 underline-offset-[3px] decoration-1 hover:decoration-[#1E3F33] transition-colors"
          >
            Start pilot
          </Link>
          <Link
            to="/request-access"
            data-testid="explore-header-request-access"
            className="hidden sm:inline-flex text-[12.5px] text-[#1E3F33] font-medium hover:text-[#173127] underline decoration-[#8FA998]/60 underline-offset-[3px] decoration-1 hover:decoration-[#1E3F33] transition-colors"
          >
            Request access
          </Link>
          <Link
            to={signInTo}
            data-testid="explore-header-signin"
            className="inline-flex items-center h-9 px-3.5 rounded-lg bg-[#1E3F33] hover:bg-[#173127] text-white text-[12.5px] font-medium tracking-tight transition-colors"
          >
            {signInLabel}
          </Link>
        </div>
      </div>
    </header>
  );
}

function SubNav() {
  const links = [
    { href: "#overview", label: "Overview" },
    { href: "#for-admins", label: "For Admins" },
    { href: "#for-providers", label: "For Providers" },
    { href: "#reports-trends", label: "Reports & Trends" },
    { href: "#schedule-fidelity", label: "Service Fidelity" },
    { href: "#pilot", label: "Request Access" },
  ];
  return (
    <nav
      data-testid="explore-subnav"
      className="sticky top-14 sm:top-16 z-30 bg-[#F7F5EF]/95 backdrop-blur border-b border-[#EDEAE0]"
    >
      <div className="mx-auto max-w-6xl px-3 sm:px-8 h-10 sm:h-11 flex items-center overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <ul className="flex items-center gap-1 sm:gap-2 whitespace-nowrap">
          {links.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                data-testid={`explore-subnav-${l.href.slice(1)}`}
                className="px-2.5 sm:px-3 py-1.5 rounded-full text-[11.5px] sm:text-[12px] text-[#4B5563] hover:text-[#1E3F33] hover:bg-[#E8F0EB] font-medium tracking-tight transition-colors"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}

function SectionShell({ id, eyebrow, title, children, lead, tone = "default" }) {
  const bg = tone === "tint" ? "bg-[#F2EFE7]" : "bg-[#F7F5EF]";
  return (
    <section
      id={id}
      data-testid={`explore-section-${id}`}
      className={`${bg} border-b border-[#EDEAE0] scroll-mt-24`}
    >
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-12 sm:py-16 lg:py-20">
        {eyebrow && (
          <div className="text-[11px] uppercase tracking-[0.16em] text-[#1E3F33] font-semibold mb-3">
            {eyebrow}
          </div>
        )}
        {title && (
          <h2 className="text-[26px] sm:text-[32px] lg:text-[36px] font-semibold text-[#1F2937] heading-font leading-[1.15] max-w-3xl">
            {title}
          </h2>
        )}
        {lead && (
          <p className="mt-4 text-[14.5px] sm:text-[15.5px] text-[#4B5563] leading-relaxed max-w-3xl">
            {lead}
          </p>
        )}
        <div className="mt-8 sm:mt-10">{children}</div>
      </div>
    </section>
  );
}

function FeatureCard({ icon, title, body, testId }) {
  return (
    <div
      data-testid={testId}
      className="group rounded-2xl border border-[#E5E3DB] bg-white p-5 sm:p-6 hover:border-[#8FA998] hover:shadow-[0_4px_20px_rgba(31,63,51,0.06)] transition-all"
    >
      <div className="w-10 h-10 rounded-xl bg-[#E8F0EB] flex items-center justify-center mb-4 group-hover:bg-[#D7E6DC] transition-colors">
        {icon}
      </div>
      <div className="text-[14.5px] font-semibold text-[#1F2937] heading-font mb-1.5 leading-tight">
        {title}
      </div>
      <p className="text-[12.5px] sm:text-[13px] text-[#4B5563] leading-relaxed">
        {body}
      </p>
    </div>
  );
}

function BulletList({ items, columns = 2, testId }) {
  return (
    <ul
      data-testid={testId}
      className={`grid grid-cols-1 ${columns === 2 ? "sm:grid-cols-2" : "sm:grid-cols-3"} gap-x-8 gap-y-2.5`}
    >
      {items.map((it, i) => (
        <li
          key={i}
          className="flex items-start gap-2.5 text-[13.5px] text-[#374151] leading-relaxed"
        >
          <CheckCircle size={16} weight="duotone" className="text-[#1E3F33] mt-0.5 flex-shrink-0" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function PreviewCard({ label, children, testId, className = "" }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl border border-[#E5E3DB] bg-white shadow-[0_2px_14px_rgba(31,41,55,0.04)] overflow-hidden ${className}`}
    >
      <div className="px-4 sm:px-5 py-3 bg-[#FBFAF6] border-b border-[#EDEAE0] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#E5C390]" />
          <div className="w-2 h-2 rounded-full bg-[#D7E6DC]" />
          <div className="w-2 h-2 rounded-full bg-[#E5E3DB]" />
        </div>
        <span className="text-[10.5px] uppercase tracking-[0.14em] text-[#8FA998] font-semibold">
          {label}
        </span>
      </div>
      <div className="p-4 sm:p-5">{children}</div>
    </div>
  );
}

// ── Hero ─────────────────────────────────────────────────────────

function Hero({ signInLabel, signInTo }) {
  return (
    <section
      id="overview"
      data-testid="explore-hero"
      className="relative overflow-hidden border-b border-[#EDEAE0] scroll-mt-24"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -right-20 w-[520px] h-[520px] rounded-full bg-[#E8F0EB] blur-3xl opacity-60" />
        <div className="absolute top-40 -left-32 w-[360px] h-[360px] rounded-full bg-[#F1E7D3] blur-3xl opacity-50" />
      </div>
      <div className="relative mx-auto max-w-6xl px-5 sm:px-8 py-14 sm:py-20 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-12 lg:gap-16 items-center">
          <div>
            <div
              data-testid="explore-invite-pill"
              className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.14em] text-[#1E3F33] bg-[#E8F0EB] rounded-full px-2.5 py-1 mb-4 font-semibold"
            >
              <ShieldCheck size={11} weight="duotone" />
              Solo pilot available · School/team invite-only
            </div>
            <h1
              data-testid="explore-hero-title"
              className="text-[36px] sm:text-[44px] lg:text-[52px] font-semibold text-[#1F2937] heading-font leading-[1.05] tracking-tight"
            >
              Frame <span className="text-[#1E3F33]">Practice</span>
            </h1>
            <p
              data-testid="explore-hero-subtitle"
              className="mt-3 text-[16px] sm:text-[18px] text-[#1E3F33] font-medium heading-font max-w-2xl leading-snug"
            >
              Provider workspace for IEP implementation, service fidelity,
              goal progress, and team coordination.
            </p>
            <p
              data-testid="explore-hero-description"
              className="mt-5 text-[14.5px] sm:text-[15.5px] text-[#4B5563] leading-relaxed max-w-2xl"
            >
              Frame Practice gives schools and providers one connected
              workspace to manage IEP goals, service schedules, data
              collection, provider caseloads, student progress, and
              administrative oversight across the full support team.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
              {/* FP3-079 — Primary CTA now points solo providers straight
                  at the /start-pilot self-serve flow. Request access
                  stays for school/team/district admins as the secondary
                  path. */}
              <Link
                to="/start-pilot"
                data-testid="explore-hero-primary-cta"
                className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg bg-[#1E3F33] hover:bg-[#173127] text-white text-[14px] font-medium tracking-tight transition-colors"
              >
                Start a Pilot Workspace
                <ArrowRight size={16} weight="bold" />
              </Link>
              <Link
                to="/request-access"
                data-testid="explore-hero-secondary-cta"
                className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg border border-[#1E3F33] text-[#1E3F33] hover:bg-[#E8F0EB] text-[14px] font-medium tracking-tight transition-colors"
              >
                Request School/Team Access
              </Link>
              <Link
                to={signInTo}
                data-testid="explore-hero-signin-cta"
                className="inline-flex items-center justify-center gap-2 h-11 px-5 text-[#1E3F33] hover:text-[#173127] text-[14px] font-medium tracking-tight transition-colors underline decoration-[#8FA998]/60 underline-offset-[4px] decoration-1 hover:decoration-[#1E3F33]"
              >
                {signInLabel}
              </Link>
            </div>
            <p className="mt-4 text-[11.5px] text-[#6B7280] leading-relaxed">
              Or email{" "}
              <a
                data-testid="explore-hero-contact-email"
                href={`mailto:${CONTACT_EMAIL}`}
                className="text-[#1E3F33] font-medium underline decoration-[#8FA998]/60 underline-offset-[3px] decoration-1 hover:decoration-[#1E3F33]"
              >
                {CONTACT_EMAIL}
              </a>{" "}
              with questions about Frame Practice.
            </p>
          </div>

          <HeroMock />
        </div>
      </div>
    </section>
  );
}

function HeroMock() {
  return (
    <div className="relative">
      <div className="absolute -bottom-6 -right-6 w-56 h-56 rounded-3xl bg-[#1E3F33]/5 -z-10" />
      <PreviewCard label="Admin Student Overview · preview" testId="explore-hero-mock">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#8FA998] font-semibold">
              Student Overview
            </div>
            <div className="text-[16px] font-semibold text-[#1F2937] heading-font leading-tight">
              Student A · Grade 3
            </div>
            <div className="text-[11.5px] text-[#6B7280] mt-0.5">
              ADMIN VIEW · 2 PROVIDERS
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#8FA998] font-semibold">
              IEP due
            </div>
            <div className="text-[12.5px] text-[#1F2937] font-medium">Apr 14</div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 mb-4">
          <MockStat label="Goals" value="6" />
          <MockStat label="Sessions · 30d" value="22" />
          <MockStat label="Fidelity" value="On track" tone="ok" />
        </div>
        <div className="space-y-2">
          <MockGoalRow name="Goal Area 1 · Articulation /s/" trend="+18%" pct={72} />
          <MockGoalRow name="Goal Area 2 · Reading fluency" trend="+9%" pct={54} />
          <MockGoalRow name="Goal Area 3 · Self-regulation" trend="—" pct={31} stale />
        </div>
      </PreviewCard>
    </div>
  );
}

function MockStat({ label, value, tone = "default" }) {
  const colour =
    tone === "ok" ? "text-[#1E3F33] bg-[#E8F0EB]" : "text-[#1F2937] bg-[#FBFAF6]";
  return (
    <div className={`rounded-lg ${colour} px-2.5 py-2 border border-[#EDEAE0]`}>
      <div className="text-[9.5px] uppercase tracking-[0.14em] text-[#6B7280] font-semibold">
        {label}
      </div>
      <div className="text-[14px] font-semibold heading-font">{value}</div>
    </div>
  );
}

function MockGoalRow({ name, trend, pct, stale }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 border-b border-[#F1F2F4] last:border-0">
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] text-[#1F2937] truncate">{name}</div>
        <div className="mt-1 h-1.5 rounded-full bg-[#F1F2F4] overflow-hidden">
          <div
            className={`h-full ${stale ? "bg-[#E5C390]" : "bg-[#1E3F33]"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
      <div
        className={`text-[11px] font-semibold ${stale ? "text-[#9C6B2E]" : "text-[#1E3F33]"} min-w-[44px] text-right`}
      >
        {trend}
      </div>
    </div>
  );
}

// ── Section: Choose Your View (FP3-047B) ─────────────────────────

function SectionChooseYourView() {
  return (
    <section
      id="choose-view"
      data-testid="explore-section-choose-view"
      className="bg-[#F7F5EF] border-b border-[#EDEAE0]"
    >
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-12 sm:py-16">
        <div className="text-[11px] uppercase tracking-[0.16em] text-[#1E3F33] font-semibold mb-3">
          Choose your view
        </div>
        <h2 className="text-[24px] sm:text-[30px] lg:text-[34px] font-semibold text-[#1F2937] heading-font leading-[1.15] max-w-2xl">
          Two ways to take the tour
        </h2>
        <p className="mt-3 text-[14px] sm:text-[15px] text-[#4B5563] leading-relaxed max-w-3xl">
          Frame Practice serves the whole team. Jump to the tour that matches how
          you'll use the workspace — administrators leading the program, or
          providers running the day.
        </p>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {/* For Administrators — deep green */}
          <a
            href="#for-admins"
            data-testid="explore-audience-admin-card"
            className="group relative overflow-hidden rounded-2xl bg-[#1E3F33] text-white p-6 sm:p-7 hover:bg-[#173127] transition-colors"
          >
            <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-[#8FA998]/10 pointer-events-none" />
            <div className="relative">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-[#8FA998]/20 flex items-center justify-center">
                  <Buildings size={20} weight="duotone" className="text-[#D7E6DC]" />
                </div>
                <div className="text-[10.5px] uppercase tracking-[0.16em] text-[#8FA998] font-semibold">
                  Administrators
                </div>
              </div>
              <div className="mt-5 text-[22px] sm:text-[26px] font-semibold heading-font leading-tight">
                For Administrators
              </div>
              <p className="mt-2 text-[13px] sm:text-[13.5px] text-[#D7E6DC] leading-relaxed">
                See the program: caseload trends, service fidelity, provider
                schedules, and a student-centered view across the team.
              </p>
              <ul className="mt-5 space-y-2 text-[12.5px] text-[#D7E6DC]">
                <AudienceBullet>Admin Student Overview · multi-provider view</AudienceBullet>
                <AudienceBullet>Reports & trends across students, providers, campuses</AudienceBullet>
                <AudienceBullet>Service fidelity for the whole org</AudienceBullet>
                <AudienceBullet>Read-only inspection of a provider's workspace</AudienceBullet>
              </ul>
              <div className="mt-6 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-white group-hover:text-[#D7E6DC] transition-colors">
                Take the admin tour
                <ArrowRight size={14} weight="bold" />
              </div>
            </div>
          </a>

          {/* For Providers — warm cream */}
          <a
            href="#for-providers"
            data-testid="explore-audience-provider-card"
            className="group relative overflow-hidden rounded-2xl bg-white border border-[#E5E3DB] p-6 sm:p-7 hover:border-[#8FA998] hover:shadow-[0_8px_30px_rgba(31,63,51,0.08)] transition-all"
          >
            <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-[#F1E7D3]/60 pointer-events-none" />
            <div className="relative">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-[#E8F0EB] flex items-center justify-center">
                  <Stethoscope size={20} weight="duotone" className="text-[#1E3F33]" />
                </div>
                <div className="text-[10.5px] uppercase tracking-[0.16em] text-[#1E3F33] font-semibold">
                  Providers
                </div>
              </div>
              <div className="mt-5 text-[22px] sm:text-[26px] font-semibold heading-font text-[#1F2937] leading-tight">
                For Providers
              </div>
              <p className="mt-2 text-[13px] sm:text-[13.5px] text-[#4B5563] leading-relaxed">
                Run the day: caseload, schedule, sessions, goals, and progress —
                designed around what providers actually do.
              </p>
              <ul className="mt-5 space-y-2 text-[12.5px] text-[#374151]">
                <AudienceBullet dark>Daily dashboard + today's sessions</AudienceBullet>
                <AudienceBullet dark>Live + document session workflows</AudienceBullet>
                <AudienceBullet dark>Goals, data, evidence, and reports</AudienceBullet>
                <AudienceBullet dark>Assessments + Reference Library (SLP)</AudienceBullet>
              </ul>
              <div className="mt-6 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#1E3F33] group-hover:text-[#173127] transition-colors">
                Take the provider tour
                <ArrowRight size={14} weight="bold" />
              </div>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}

function AudienceBullet({ children, dark }) {
  return (
    <li className="flex items-start gap-2">
      <CheckCircle
        size={14}
        weight="duotone"
        className={`mt-0.5 flex-shrink-0 ${dark ? "text-[#1E3F33]" : "text-[#8FA998]"}`}
      />
      <span>{children}</span>
    </li>
  );
}

// ── Section: What We Organize ────────────────────────────────────

function SectionWhatWeOrganize() {
  return (
    <SectionShell
      id="what-we-organize"
      eyebrow="What we help organize"
      title="One connected workspace for IEP service delivery"
      lead="Schools often manage IEP service delivery across scattered spreadsheets, calendars, PDFs, provider notes, progress reports, and emails. Frame Practice brings these workflows into one connected workspace so providers, admins, and teams can stay aligned."
      tone="tint"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
        <FeatureCard testId="explore-feature-caseloads"
          icon={<Users size={20} weight="duotone" className="text-[#1E3F33]" />}
          title="Student caseloads"
          body="Provider-attributed caseloads with primary, direct-service, evaluation, and consult roles." />
        <FeatureCard testId="explore-feature-iep-goals"
          icon={<Target size={20} weight="duotone" className="text-[#1E3F33]" />}
          title="IEP goal development"
          body="Measurable goals built from observable behavior, conditions, criteria, and progress markers." />
        <FeatureCard testId="explore-feature-data-collection"
          icon={<ChartLineUp size={20} weight="duotone" className="text-[#1E3F33]" />}
          title="Data collection & progress"
          body="Per-trial, percent, and narrative data captured during or after sessions and linked back to the goal." />
        <FeatureCard testId="explore-feature-schedules"
          icon={<CalendarCheck size={20} weight="duotone" className="text-[#1E3F33]" />}
          title="Service schedules"
          body="Provider calendars, student service blocks, and shared visibility for the wider team." />
        <FeatureCard testId="explore-feature-fidelity"
          icon={<Clock size={20} weight="duotone" className="text-[#1E3F33]" />}
          title="Service minutes & fidelity"
          body="Expected vs delivered minutes, attendance aware, with make-up needs surfaced for the team." />
        <FeatureCard testId="explore-feature-reports-trends"
          icon={<ChartBar size={20} weight="duotone" className="text-[#1E3F33]" />}
          title="Reports & trends"
          body="Cross-student and cross-provider trends for planning meetings, MTSS, and admin review." />
        <FeatureCard testId="explore-feature-team"
          icon={<HandHeart size={20} weight="duotone" className="text-[#1E3F33]" />}
          title="Provider / team coordination"
          body="Team notes, supervision relationships, and shared student context across providers." />
        <FeatureCard testId="explore-feature-oversight"
          icon={<ShieldCheck size={20} weight="duotone" className="text-[#1E3F33]" />}
          title="Admin oversight"
          body="Read-only oversight that preserves provider attribution while giving admins the full picture." />
        <FeatureCard testId="explore-feature-documents"
          icon={<FilePdf size={20} weight="duotone" className="text-[#1E3F33]" />}
          title="Documents & assessments"
          body="Centralized assessment artifacts and documents linked to each student's record." />
      </div>
    </SectionShell>
  );
}

// ── Section: Reports & Trends — major visual upgrade ──────────────

function SectionReportsTrends() {
  return (
    <SectionShell
      id="reports-trends"
      eyebrow="Reports & trends · special focus"
      title="Reports and trends that show what is actually happening"
      lead="Frame Practice helps teams move beyond scattered data points. Administrators can view trends across students, providers, goals, services, and campuses to see where students are improving, where data is missing, and where service delivery needs attention."
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">
        {/* Big trend graph card — spans 2 cols on lg */}
        <PreviewCard label="Goal progress trend · 8 weeks" testId="explore-reports-trend-card" className="lg:col-span-2">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#8FA998] font-semibold">
                Service Area · Speech · Goal Area 1
              </div>
              <div className="text-[16px] font-semibold text-[#1F2937] heading-font leading-tight">
                Cohort accuracy · +24% over 8 weeks
              </div>
            </div>
            <FilterRow />
          </div>
          <TrendChart
            seriesA={[28, 33, 38, 44, 49, 54, 58, 63]}
            seriesB={[22, 26, 31, 35, 38, 42, 47, 52]}
          />
          <div className="mt-3 flex items-center gap-5 text-[11px] text-[#6B7280]">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-3 h-1 rounded bg-[#1E3F33]" /> Campus A · 12 students
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-3 h-1 rounded bg-[#9C6B2E]" /> Campus B · 9 students
            </span>
          </div>
        </PreviewCard>

        {/* Status cards stack */}
        <PreviewCard label="Caseload health · last 30 days" testId="explore-reports-status-card">
          <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#8FA998] font-semibold">
            Across 18 students
          </div>
          <div className="text-[15px] font-semibold text-[#1F2937] heading-font mb-3">
            6 needing attention
          </div>
          <div className="space-y-2">
            <TrendBar label="On track" value={9} tone="ok" />
            <TrendBar label="Slow progress" value={6} tone="warn" />
            <TrendBar label="No recent data" value={3} tone="stale" />
          </div>
        </PreviewCard>
      </div>

      {/* Lower row — three smaller cards */}
      <div className="mt-4 sm:mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
        {/* Service fidelity sparkline */}
        <PreviewCard label="Service fidelity trend · 6 weeks" testId="explore-reports-fidelity-trend">
          <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#8FA998] font-semibold">
            Org-wide
          </div>
          <div className="flex items-baseline gap-2 mb-3">
            <div className="text-[20px] font-semibold text-[#1E3F33] heading-font">92%</div>
            <div className="text-[11px] text-[#1E3F33] font-medium">+4% vs prior 6 wk</div>
          </div>
          <SparklineSvg points={[78, 82, 84, 86, 90, 92]} colour="#1E3F33" />
        </PreviewCard>

        {/* Students needing attention card */}
        <PreviewCard label="Students needing attention" testId="explore-reports-attention-card">
          <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#8FA998] font-semibold">
            6 of 18 students
          </div>
          <div className="text-[15px] font-semibold text-[#1F2937] heading-font mb-3">
            Sorted by priority
          </div>
          <ul className="space-y-2">
            <AttentionRow name="Student A · Grade 3" reason="IEP overdue · 12 days" tone="danger" />
            <AttentionRow name="Student B · Grade 5" reason="No data · 14 days" tone="warn" />
            <AttentionRow name="Student C · Grade 2" reason="Fidelity 71%" tone="warn" />
          </ul>
        </PreviewCard>

        {/* Data recency card */}
        <PreviewCard label="Goal data recency" testId="explore-reports-recency-card">
          <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#8FA998] font-semibold">
            Last data captured
          </div>
          <div className="text-[15px] font-semibold text-[#1F2937] heading-font mb-3">
            Across 22 active goals
          </div>
          <div className="space-y-2.5">
            <RecencyRow label="Within 7 days" value={14} total={22} tone="ok" />
            <RecencyRow label="8–14 days" value={5} total={22} tone="warn" />
            <RecencyRow label="No recent data" value={3} total={22} tone="stale" />
          </div>
        </PreviewCard>
      </div>

      {/* How admins use trends */}
      <div className="mt-8 sm:mt-10 rounded-2xl border border-[#E5E3DB] bg-white p-5 sm:p-6">
        <div className="text-[11px] uppercase tracking-[0.14em] text-[#1E3F33] font-semibold mb-2">
          How administrators use trends
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
          <TrendUseCard
            number="01"
            title="Spot data gaps early"
            body="Find students whose goals haven't seen data in two or more weeks before the next progress report cycle."
          />
          <TrendUseCard
            number="02"
            title="See provider load"
            body="Compare delivered minutes, caseload size, and make-up needs across providers and campuses."
          />
          <TrendUseCard
            number="03"
            title="Prepare for meetings"
            body="Pull a student-centered or program-wide snapshot before annual reviews, MTSS meetings, and admin check-ins."
          />
        </div>
      </div>
    </SectionShell>
  );
}

function FilterRow() {
  return (
    <div
      data-testid="explore-reports-filter-mock"
      className="flex items-center gap-2 text-[10.5px] text-[#1E3F33]"
    >
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#E8F0EB] font-semibold uppercase tracking-[0.12em]">
        <Funnel size={10} weight="duotone" /> filters
      </span>
      <span className="px-2 py-1 rounded-full border border-[#E5E3DB] bg-white">Campus: All</span>
      <span className="px-2 py-1 rounded-full border border-[#E5E3DB] bg-white">Provider: All</span>
      <span className="px-2 py-1 rounded-full border border-[#1E3F33] bg-[#1E3F33] text-white">Last 8 wk</span>
    </div>
  );
}

function TrendChart({ seriesA, seriesB }) {
  const max = Math.max(...seriesA, ...seriesB, 80);
  const w = 320;
  const h = 100;
  const stepX = w / (seriesA.length - 1);
  const toPath = (pts) =>
    pts
      .map((p, i) => `${i === 0 ? "M" : "L"}${(i * stepX).toFixed(2)},${(h - (p / max) * h).toFixed(2)}`)
      .join(" ");
  const pathA = toPath(seriesA);
  const pathB = toPath(seriesB);
  const areaA = `${pathA} L${w},${h} L0,${h} Z`;
  return (
    <div className="mt-4">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-32 overflow-visible">
        {/* Gridlines */}
        {[0.25, 0.5, 0.75].map((y) => (
          <line
            key={y}
            x1={0}
            x2={w}
            y1={h * y}
            y2={h * y}
            stroke="#EDEAE0"
            strokeWidth="0.5"
            strokeDasharray="2 3"
          />
        ))}
        <path d={areaA} fill="#E8F0EB" />
        <path d={pathA} stroke="#1E3F33" strokeWidth="1.6" fill="none" strokeLinecap="round" />
        <path d={pathB} stroke="#9C6B2E" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeDasharray="3 2" />
        {seriesA.map((p, i) => (
          <circle key={`a${i}`} cx={i * stepX} cy={h - (p / max) * h} r={1.8} fill="#1E3F33" />
        ))}
        {seriesB.map((p, i) => (
          <circle key={`b${i}`} cx={i * stepX} cy={h - (p / max) * h} r={1.6} fill="#9C6B2E" />
        ))}
      </svg>
      <div className="mt-2 flex items-center justify-between text-[10.5px] text-[#6B7280]">
        <span>Week 1</span>
        <span>Week 8</span>
      </div>
    </div>
  );
}

function SparklineSvg({ points, colour }) {
  const max = Math.max(...points, 100);
  const w = 100;
  const h = 28;
  const stepX = w / (points.length - 1);
  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${(i * stepX).toFixed(2)},${(h - (p / max) * h).toFixed(2)}`)
    .join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-12 overflow-visible">
      <path d={`${path} L${w},${h} L0,${h} Z`} fill={`${colour}1A`} />
      <path d={path} stroke={colour} strokeWidth="1.6" fill="none" strokeLinecap="round" />
    </svg>
  );
}

function TrendBar({ label, value, tone }) {
  const colour =
    tone === "ok" ? "bg-[#1E3F33]" : tone === "warn" ? "bg-[#9C6B2E]" : "bg-[#C46155]";
  const max = 18;
  const pct = Math.round((value / max) * 100);
  return (
    <div className="flex items-center gap-3">
      <div className="text-[11.5px] text-[#6B7280] w-28 flex-shrink-0">{label}</div>
      <div className="flex-1 h-2 rounded-full bg-[#F1F2F4] overflow-hidden">
        <div className={`h-full ${colour}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="text-[12px] text-[#1F2937] font-semibold w-6 text-right">{value}</div>
    </div>
  );
}

function AttentionRow({ name, reason, tone }) {
  const iconColour =
    tone === "danger" ? "text-[#A8453A]" : tone === "warn" ? "text-[#9C6B2E]" : "text-[#1E3F33]";
  const Icon = tone === "danger" ? WarningCircle : ClockCountdown;
  return (
    <li className="flex items-start gap-2 py-1.5 border-b border-[#F1F2F4] last:border-0">
      <Icon size={14} weight="duotone" className={`${iconColour} mt-0.5 flex-shrink-0`} />
      <div className="min-w-0 flex-1">
        <div className="text-[12px] text-[#1F2937] truncate font-medium">{name}</div>
        <div className="text-[10.5px] text-[#6B7280]">{reason}</div>
      </div>
    </li>
  );
}

function RecencyRow({ label, value, total, tone }) {
  const colour =
    tone === "ok" ? "bg-[#1E3F33]" : tone === "warn" ? "bg-[#9C6B2E]" : "bg-[#C46155]";
  const pct = Math.round((value / total) * 100);
  return (
    <div>
      <div className="flex items-center justify-between text-[11.5px] text-[#1F2937] mb-1">
        <span>{label}</span>
        <span className="font-semibold">{value} / {total}</span>
      </div>
      <div className="h-1.5 rounded-full bg-[#F1F2F4] overflow-hidden">
        <div className={`h-full ${colour}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function TrendUseCard({ number, title, body }) {
  return (
    <div className="flex items-start gap-3">
      <div className="text-[20px] font-semibold heading-font text-[#8FA998] leading-none">
        {number}
      </div>
      <div className="min-w-0">
        <div className="text-[13.5px] font-semibold text-[#1F2937] heading-font leading-tight">
          {title}
        </div>
        <p className="mt-1 text-[12px] text-[#4B5563] leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

// ── Section: For Administrators ──────────────────────────────────

function SectionForAdmins() {
  return (
    <section
      id="for-admins"
      data-testid="explore-section-for-admins"
      className="bg-[#1E3F33] text-white border-b border-[#173127] scroll-mt-24"
    >
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-14 sm:py-20">
        <div className="text-[11px] uppercase tracking-[0.16em] text-[#8FA998] font-semibold mb-3">
          For Administrators
        </div>
        <h2 className="text-[26px] sm:text-[32px] lg:text-[36px] font-semibold heading-font leading-[1.15] max-w-3xl">
          See the program — not just one student at a time
        </h2>
        <p className="mt-4 text-[14.5px] sm:text-[15.5px] text-[#D7E6DC] leading-relaxed max-w-3xl">
          Frame Practice is built for the people leading the program. Get a
          student-centered view of the whole team, see service fidelity in one
          place, and inspect any provider's workspace read-only — without
          overwriting their work.
        </p>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          <AdminTourCard
            testId="explore-admin-tour-dashboard"
            icon={<House size={18} weight="duotone" />}
            title="School-wide dashboard"
            body="Org snapshot: students, providers, alerts, and the data points admins look at first thing in the morning."
            mock={<DashboardMock />}
          />
          <AdminTourCard
            testId="explore-admin-tour-overview"
            icon={<Compass size={18} weight="duotone" />}
            title="Admin Student Overview"
            body="Student-centered, multi-provider view bringing demographics, services, goals, fidelity, and team notes together."
            mock={<StudentOverviewMock />}
          />
          <AdminTourCard
            testId="explore-admin-tour-fidelity"
            icon={<ChartPieSlice size={18} weight="duotone" />}
            title="Service Fidelity"
            body="Org-wide expected vs delivered minutes, attendance-aware, with make-up needs surfaced per student and provider."
            mock={<FidelityRollupMock />}
          />
          <AdminTourCard
            testId="explore-admin-tour-schedules"
            icon={<CalendarCheck size={18} weight="duotone" />}
            title="Provider Schedules"
            body="Shared calendar visibility across the team. Spot conflicts and uncovered service blocks at a glance."
            mock={<ScheduleGridMock />}
          />
          <AdminTourCard
            testId="explore-admin-tour-reports"
            icon={<ChartBar size={18} weight="duotone" />}
            title="Reports & Trends"
            body="Trends across students, providers, goals, services, and campuses — exportable for meetings and admin review."
            mock={<TinyTrendMock />}
          />
          <AdminTourCard
            testId="explore-admin-tour-readonly"
            icon={<Eye size={18} weight="duotone" />}
            title="Read-only Provider Workspace"
            body="Inspect any provider's day, sessions, and progress without overwriting their documentation. Provider-authored work stays attributed."
            mock={<ReadOnlyBannerMock />}
          />
        </div>

        <div className="mt-8 flex items-center gap-3 flex-wrap">
          <Link
            to="/request-access"
            data-testid="explore-admin-cta"
            className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg bg-white text-[#1E3F33] hover:bg-[#F7F5EF] text-[14px] font-medium tracking-tight transition-colors"
          >
            Request access
            <ArrowRight size={16} weight="bold" />
          </Link>
          <a
            href="#for-providers"
            data-testid="explore-admin-jump-to-providers"
            className="text-[12.5px] text-[#D7E6DC] hover:text-white font-medium underline decoration-[#8FA998]/60 underline-offset-[3px] decoration-1 hover:decoration-white transition-colors"
          >
            Or see the provider tour →
          </a>
        </div>
      </div>
    </section>
  );
}

function AdminTourCard({ icon, title, body, mock, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl bg-[#173127] border border-[#2C5042] p-5 hover:border-[#8FA998] transition-colors"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-xl bg-[#8FA998]/20 flex items-center justify-center text-[#D7E6DC]">
          {icon}
        </div>
        <div className="text-[14px] font-semibold heading-font text-white leading-tight">
          {title}
        </div>
      </div>
      <p className="text-[12px] text-[#D7E6DC] leading-relaxed mb-4">{body}</p>
      <div className="rounded-xl bg-[#0F231C] border border-[#2C5042] p-3">{mock}</div>
    </div>
  );
}

// Admin mocks
function DashboardMock() {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2">
        <SmallStat label="Students" value="142" />
        <SmallStat label="Providers" value="12" />
        <SmallStat label="Alerts" value="6" tone="warn" />
      </div>
      <div className="h-1 rounded-full bg-[#1E3F33] overflow-hidden">
        <div className="h-full bg-[#8FA998]" style={{ width: "78%" }} />
      </div>
      <div className="text-[9.5px] uppercase tracking-[0.12em] text-[#8FA998] font-semibold">
        Service fidelity · 78% on track
      </div>
    </div>
  );
}

function StudentOverviewMock() {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] font-semibold text-white">Student A · Grade 3</div>
        <span className="text-[8.5px] uppercase tracking-[0.12em] text-[#8FA998] font-semibold">2 providers</span>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <DarkChip label="Goals · 6" />
        <DarkChip label="Fidelity · 92%" tone="ok" />
        <DarkChip label="IEP · Apr 14" />
        <DarkChip label="Team · 2" />
      </div>
    </div>
  );
}

function FidelityRollupMock() {
  return (
    <div className="space-y-2">
      <DarkRow label="Speech" delivered={48} expected={50} />
      <DarkRow label="OT" delivered={26} expected={30} />
      <DarkRow label="Reading" delivered={42} expected={60} />
    </div>
  );
}

function ScheduleGridMock() {
  return (
    <div className="grid grid-cols-5 gap-1">
      {Array.from({ length: 15 }).map((_, i) => (
        <div
          key={i}
          className={`h-4 rounded ${i % 5 === 4 ? "bg-[#2C5042]" : i % 6 === 0 ? "bg-[#9C6B2E]/70" : "bg-[#8FA998]"}`}
        />
      ))}
    </div>
  );
}

function TinyTrendMock() {
  return (
    <svg viewBox="0 0 100 30" className="w-full h-10 overflow-visible">
      <path
        d="M0,22 L14,18 L28,14 L42,11 L56,9 L70,7 L84,6 L100,4"
        stroke="#8FA998"
        strokeWidth="1.6"
        fill="none"
        strokeLinecap="round"
      />
      <path
        d="M0,22 L14,18 L28,14 L42,11 L56,9 L70,7 L84,6 L100,4 L100,30 L0,30 Z"
        fill="#8FA998"
        fillOpacity="0.25"
      />
    </svg>
  );
}

function ReadOnlyBannerMock() {
  return (
    <div className="rounded-md bg-[#9C6B2E]/15 border border-[#9C6B2E]/40 px-3 py-2">
      <div className="text-[9.5px] uppercase tracking-[0.12em] text-[#E5C390] font-semibold">
        Admin View · Read Only
      </div>
      <div className="text-[10.5px] text-[#F1E7D3] font-medium leading-tight mt-0.5">
        Viewing Provider 1's workspace
      </div>
    </div>
  );
}

function SmallStat({ label, value, tone = "default" }) {
  const colour =
    tone === "warn" ? "bg-[#9C6B2E]/20 text-[#E5C390]" : "bg-[#1E3F33] text-[#D7E6DC]";
  return (
    <div className={`rounded-md ${colour} px-2 py-1.5 border border-[#2C5042]`}>
      <div className="text-[8.5px] uppercase tracking-[0.12em] opacity-80">{label}</div>
      <div className="text-[12px] font-semibold heading-font text-white">{value}</div>
    </div>
  );
}

function DarkChip({ label, tone = "default" }) {
  const colour =
    tone === "ok" ? "bg-[#8FA998]/20 text-[#D7E6DC]" : "bg-[#1E3F33] text-[#D7E6DC]";
  return (
    <div className={`rounded-md ${colour} px-2 py-1 border border-[#2C5042] text-[9.5px] font-medium`}>
      {label}
    </div>
  );
}

function DarkRow({ label, delivered, expected }) {
  const pct = Math.round((delivered / expected) * 100);
  const tone = pct >= 95 ? "ok" : pct >= 80 ? "warn" : "stale";
  const colour =
    tone === "ok" ? "bg-[#8FA998]" : tone === "warn" ? "bg-[#9C6B2E]" : "bg-[#C46155]";
  return (
    <div>
      <div className="flex items-center justify-between text-[10px] text-[#D7E6DC] mb-1">
        <span>{label}</span>
        <span className="font-semibold">{pct}%</span>
      </div>
      <div className="h-1 rounded-full bg-[#0F231C] overflow-hidden">
        <div className={`h-full ${colour}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
    </div>
  );
}

// ── Section: For Providers ───────────────────────────────────────

function SectionForProviders() {
  return (
    <section
      id="for-providers"
      data-testid="explore-section-for-providers"
      className="bg-[#F7F5EF] border-b border-[#EDEAE0] scroll-mt-24"
    >
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-14 sm:py-20">
        <div className="text-[11px] uppercase tracking-[0.16em] text-[#1E3F33] font-semibold mb-3">
          For Providers
        </div>
        <h2 className="text-[26px] sm:text-[32px] lg:text-[36px] font-semibold text-[#1F2937] heading-font leading-[1.15] max-w-3xl">
          Designed around the provider's day
        </h2>
        <p className="mt-4 text-[14.5px] sm:text-[15.5px] text-[#4B5563] leading-relaxed max-w-3xl">
          Frame Practice keeps providers in flow. A calm daily dashboard, fewer
          tabs, sessions documented as you go, and progress that's always
          attributed to the provider who did the work.
        </p>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          <ProviderTourCard
            testId="explore-provider-tour-today"
            icon={<House size={18} weight="duotone" className="text-[#1E3F33]" />}
            title="Today dashboard"
            body="Your day at a glance: today's sessions, due-soon goals, overdue items, and attention-needed students."
            mock={<TodayMock />}
          />
          <ProviderTourCard
            testId="explore-provider-tour-caseload"
            icon={<Users size={18} weight="duotone" className="text-[#1E3F33]" />}
            title="Caseload"
            body="Your students with role, service area, and primary/eval/consult flags — only your caseload, no clutter."
            mock={<CaseloadMock />}
          />
          <ProviderTourCard
            testId="explore-provider-tour-schedule"
            icon={<CalendarCheck size={18} weight="duotone" className="text-[#1E3F33]" />}
            title="Schedule"
            body="Provider calendar, service blocks, school calendar exceptions, and unavailability — built into the day."
            mock={<ProviderScheduleMock />}
          />
          <ProviderTourCard
            testId="explore-provider-tour-session"
            icon={<ClipboardText size={18} weight="duotone" className="text-[#1E3F33]" />}
            title="Live + document session"
            body="Capture data during a session or document it after the fact. Multi-goal sessions, narrative notes, trial data."
            mock={<LiveSessionMock />}
          />
          <ProviderTourCard
            testId="explore-provider-tour-progress"
            icon={<ChartLineUp size={18} weight="duotone" className="text-[#1E3F33]" />}
            title="Goals & progress"
            body="Active goals by service area, trends over time, evidence linked back to the goal, and ready-to-share progress."
            mock={<ProviderProgressMock />}
          />
          <ProviderTourCard
            testId="explore-provider-tour-fidelity"
            icon={<Clock size={18} weight="duotone" className="text-[#1E3F33]" />}
            title="Service fidelity"
            body="See your own delivered vs expected minutes, make-up needs, and which students are slipping behind."
            mock={<ProviderFidelityMock />}
          />
          <ProviderTourCard
            testId="explore-provider-tour-assessments"
            icon={<Books size={18} weight="duotone" className="text-[#1E3F33]" />}
            title="Assessments & Reference Library"
            body="Assessment runner, articulation screener with a student-facing viewer, and the SLP Reference Library where applicable."
            mock={<AssessmentMock />}
          />
        </div>

        <div className="mt-8 flex items-center gap-3 flex-wrap">
          {/* FP3-079 — Provider tour speaks to solo SLPs/OTs/PTs most
              naturally; primary CTA points them at the self-serve
              pilot flow. Request access stays as the school/team
              path. */}
          <Link
            to="/start-pilot"
            data-testid="explore-provider-cta"
            className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg bg-[#1E3F33] hover:bg-[#173127] text-white text-[14px] font-medium tracking-tight transition-colors"
          >
            Start a Pilot Workspace
            <ArrowRight size={16} weight="bold" />
          </Link>
          <Link
            to="/request-access"
            data-testid="explore-provider-cta-secondary"
            className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg border border-[#1E3F33] text-[#1E3F33] hover:bg-[#E8F0EB] text-[14px] font-medium tracking-tight transition-colors"
          >
            Request School/Team Access
          </Link>
          <a
            href="#for-admins"
            data-testid="explore-provider-jump-to-admins"
            className="text-[12.5px] text-[#1E3F33] hover:text-[#173127] font-medium underline decoration-[#8FA998]/60 underline-offset-[3px] decoration-1 hover:decoration-[#1E3F33] transition-colors"
          >
            Or see the admin tour →
          </a>
        </div>
      </div>
    </section>
  );
}

function ProviderTourCard({ icon, title, body, mock, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl bg-white border border-[#E5E3DB] p-5 hover:border-[#8FA998] hover:shadow-[0_4px_20px_rgba(31,63,51,0.06)] transition-all"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-xl bg-[#E8F0EB] flex items-center justify-center">
          {icon}
        </div>
        <div className="text-[14px] font-semibold heading-font text-[#1F2937] leading-tight">
          {title}
        </div>
      </div>
      <p className="text-[12px] text-[#4B5563] leading-relaxed mb-4">{body}</p>
      <div className="rounded-xl bg-[#FBFAF6] border border-[#EDEAE0] p-3">{mock}</div>
    </div>
  );
}

// Provider mocks
function TodayMock() {
  return (
    <div className="space-y-1.5">
      <LightRow time="9:00" label="Student A · 30 min" tone="done" />
      <LightRow time="10:15" label="Group · Lang · 45 min" tone="done" />
      <LightRow time="1:00" label="Student B · 30 min" tone="next" />
      <LightRow time="2:30" label="Student C · 30 min" />
    </div>
  );
}

function LightRow({ time, label, tone = "default" }) {
  const dotColour =
    tone === "done" ? "bg-[#1E3F33]" : tone === "next" ? "bg-[#9C6B2E]" : "bg-[#D5D2C7]";
  const textColour = tone === "done" ? "text-[#6B7280] line-through" : "text-[#1F2937]";
  return (
    <div className="flex items-center gap-2">
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${dotColour}`} />
      <span className={`text-[9.5px] font-semibold tabular-nums ${tone === "next" ? "text-[#9C6B2E]" : "text-[#1E3F33]"} w-8`}>
        {time}
      </span>
      <span className={`text-[10px] ${textColour}`}>{label}</span>
    </div>
  );
}

function CaseloadMock() {
  return (
    <div className="space-y-1.5">
      <LightStudent name="Student A" role="Primary · Speech" pct={72} />
      <LightStudent name="Student B" role="Eval · Speech" pct={54} />
      <LightStudent name="Student C" role="Consult · Speech" pct={31} stale />
    </div>
  );
}

function LightStudent({ name, role, pct, stale }) {
  return (
    <div>
      <div className="flex items-center justify-between text-[10px]">
        <span className="text-[#1F2937] font-medium">{name}</span>
        <span className={`text-[9.5px] ${stale ? "text-[#9C6B2E]" : "text-[#1E3F33]"} font-semibold`}>{pct}%</span>
      </div>
      <div className="text-[8.5px] text-[#6B7280]">{role}</div>
      <div className="mt-0.5 h-0.5 rounded-full bg-[#F1F2F4] overflow-hidden">
        <div
          className={`h-full ${stale ? "bg-[#9C6B2E]" : "bg-[#1E3F33]"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function ProviderScheduleMock() {
  return (
    <div className="grid grid-cols-5 gap-1">
      {Array.from({ length: 15 }).map((_, i) => {
        const filled = i % 4 !== 3;
        return (
          <div
            key={i}
            className={`h-4 rounded ${
              filled
                ? i === 7
                  ? "bg-[#9C6B2E]"
                  : "bg-[#1E3F33]"
                : "bg-[#F1F2F4] border border-[#EDEAE0]"
            }`}
          />
        );
      })}
    </div>
  );
}

function LiveSessionMock() {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[9.5px] uppercase tracking-[0.12em] text-[#1E3F33] font-semibold">
          Live session
        </span>
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#C46155] animate-pulse" />
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        <SessionStat label="Trials" value="14" />
        <SessionStat label="Correct" value="11" tone="ok" />
        <SessionStat label="Accuracy" value="79%" tone="ok" />
      </div>
    </div>
  );
}

function SessionStat({ label, value, tone = "default" }) {
  const colour =
    tone === "ok" ? "bg-[#E8F0EB] text-[#1E3F33]" : "bg-white text-[#1F2937]";
  return (
    <div className={`rounded-md ${colour} px-1.5 py-1 border border-[#EDEAE0]`}>
      <div className="text-[8px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
        {label}
      </div>
      <div className="text-[11px] font-semibold heading-font">{value}</div>
    </div>
  );
}

function ProviderProgressMock() {
  return (
    <div>
      <div className="text-[9.5px] uppercase tracking-[0.12em] text-[#1E3F33] font-semibold mb-1">
        Goal Area 1 · /s/ in conversation
      </div>
      <div className="text-[12px] font-semibold heading-font text-[#1F2937] mb-1">
        72% · +18%
      </div>
      <SparklineSvg points={[28, 34, 41, 49, 58, 66, 72]} colour="#1E3F33" />
    </div>
  );
}

function ProviderFidelityMock() {
  return (
    <div className="space-y-1.5">
      <FidelityMini label="Student A · 60 min/wk" delivered={56} expected={60} />
      <FidelityMini label="Student B · 30 min/wk" delivered={30} expected={30} />
      <FidelityMini label="Student C · 90 min/wk" delivered={66} expected={90} />
    </div>
  );
}

function FidelityMini({ label, delivered, expected }) {
  const pct = Math.round((delivered / expected) * 100);
  const tone = pct >= 95 ? "ok" : pct >= 80 ? "warn" : "stale";
  const colour =
    tone === "ok" ? "bg-[#1E3F33]" : tone === "warn" ? "bg-[#9C6B2E]" : "bg-[#C46155]";
  return (
    <div>
      <div className="flex items-center justify-between text-[9.5px] text-[#1F2937]">
        <span>{label}</span>
        <span className="font-semibold">{pct}%</span>
      </div>
      <div className="mt-0.5 h-0.5 rounded-full bg-[#F1F2F4] overflow-hidden">
        <div className={`h-full ${colour}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
    </div>
  );
}

function AssessmentMock() {
  return (
    <div className="space-y-1.5">
      <div className="rounded-md bg-white border border-[#EDEAE0] px-2.5 py-1.5">
        <div className="text-[9.5px] uppercase tracking-[0.12em] text-[#1E3F33] font-semibold">
          Articulation Screener
        </div>
        <div className="text-[11px] font-semibold heading-font text-[#1F2937]">
          24 prompts · in progress
        </div>
      </div>
      <div className="rounded-md bg-white border border-[#EDEAE0] px-2.5 py-1.5 flex items-center justify-between">
        <span className="text-[9.5px] text-[#1F2937]">Reference Library</span>
        <span className="text-[8.5px] uppercase tracking-[0.12em] text-[#8FA998] font-semibold">SLP</span>
      </div>
    </div>
  );
}

// ── Section: Goals & Progress ────────────────────────────────────

function SectionGoalsProgress() {
  const items = [
    "Measurable IEP goal development",
    "Goal Plan / long-term development view",
    "Active goals by provider and service area",
    "Goal data over time",
    "Narrative and chartable data",
    "Evidence linked to goals",
    "Articulation, language, academic & service-area support",
    "Provider-attributed progress",
  ];
  return (
    <SectionShell
      id="goals-progress"
      eyebrow="Goals & progress"
      title="Goals teams can actually measure"
      lead="Frame Practice supports measurable IEP goal development with a long-term Goal Plan view, active goals organized by provider and service area, and progress data captured directly from sessions. Optional AI assistance can suggest drafts; the team always reviews and edits before goals reach the IEP."
      tone="tint"
    >
      <BulletList items={items} columns={2} testId="explore-goals-bullets" />
    </SectionShell>
  );
}

// ── Section: Data Collection ─────────────────────────────────────

function SectionDataCollection() {
  const items = [
    "Document completed sessions or capture data live",
    "Log multiple goals per session",
    "Track percent / trials, narrative notes, informal probes",
    "Separate live-data and document-session workflows",
    "Group session support",
    "Student attendance captured per session",
    "Provider attribution preserved on every data point",
    "Session notes and evidence history",
  ];
  return (
    <SectionShell
      id="data-collection"
      eyebrow="Data collection"
      title="Capture the work providers are already doing"
      lead="Providers can document sessions during the session or after the fact. Multiple goals can be logged on a single session, with percent / trial / narrative data tracked together. Evidence and session history stay attributed to the provider who collected it."
    >
      <BulletList items={items} columns={2} testId="explore-data-bullets" />
    </SectionShell>
  );
}

// ── Section: Scheduling & Service Fidelity ───────────────────────

function SectionScheduleFidelity() {
  const items = [
    "Provider schedules",
    "Student service blocks",
    "Service commitments per student",
    "Delivered minutes",
    "Make-up service needs",
    "Provider unavailability",
    "School calendar exceptions",
    "Admin service fidelity overview",
    "Shared calendar visibility",
    "Schedule Builder",
  ];
  return (
    <SectionShell
      id="schedule-fidelity"
      eyebrow="Scheduling & service fidelity"
      title="See whether service plans are actually being implemented"
      lead="Frame Practice helps schools see whether service plans are actually being implemented over time. Service fidelity views compare expected and delivered minutes while accounting for attendance, provider unavailability, school calendar exceptions, and make-up needs."
      tone="tint"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-8 lg:gap-12 items-start">
        <BulletList items={items} columns={2} testId="explore-schedule-bullets" />
        <PreviewCard label="Service fidelity · preview" testId="explore-fidelity-mock">
          <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#8FA998] font-semibold mb-1">
            Service Area · Speech
          </div>
          <div className="text-[15px] font-semibold text-[#1F2937] heading-font">
            Delivered vs expected
          </div>
          <div className="mt-4 space-y-3">
            <FidelityRow label="Student A · 60 min/wk" delivered={56} expected={60} />
            <FidelityRow label="Student B · 30 min/wk" delivered={30} expected={30} />
            <FidelityRow label="Student C · 90 min/wk" delivered={66} expected={90} />
          </div>
        </PreviewCard>
      </div>
    </SectionShell>
  );
}

function FidelityRow({ label, delivered, expected }) {
  const pct = Math.round((delivered / expected) * 100);
  const tone = pct >= 95 ? "ok" : pct >= 80 ? "warn" : "stale";
  const barColour =
    tone === "ok" ? "bg-[#1E3F33]" : tone === "warn" ? "bg-[#9C6B2E]" : "bg-[#C46155]";
  const chipColour =
    tone === "ok"
      ? "text-[#1E3F33] bg-[#E8F0EB]"
      : tone === "warn"
        ? "text-[#9C6B2E] bg-[#FBF3E4]"
        : "text-[#A8453A] bg-[#FBEEEE]";
  return (
    <div>
      <div className="flex items-center justify-between text-[12px] text-[#1F2937] mb-1">
        <span>{label}</span>
        <span className={`text-[10.5px] uppercase tracking-[0.1em] font-semibold rounded-full px-2 py-0.5 ${chipColour}`}>
          {pct}%
        </span>
      </div>
      <div className="h-2 rounded-full bg-[#F1F2F4] overflow-hidden">
        <div className={`h-full ${barColour}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
    </div>
  );
}

// ── Section: Team Coordination ───────────────────────────────────

function SectionTeamCoordination() {
  const items = [
    "Shared student view across the team",
    "Provider team & assignments",
    "Primary provider designation",
    "Direct service vs evaluation / consult flags",
    "Assistant / supervisor relationships (SLPA, COTA, PTA)",
    "Team notes with author attribution",
    "Admin read-only oversight",
    "Cross-provider visibility without losing provider attribution",
  ];
  return (
    <SectionShell
      id="team-coordination"
      eyebrow="Team coordination"
      title="Built for teams, not just individuals"
      lead="Frame Practice models the real shape of a school team — primaries and assistants, direct service and consult, admins and providers — so the right people see the right information without losing attribution for who actually did the work."
    >
      <BulletList items={items} columns={2} testId="explore-team-bullets" />
    </SectionShell>
  );
}

// ── Section: Assessments & Documents ─────────────────────────────

function SectionAssessmentsDocuments() {
  const items = [
    "Assessment artifacts attached to the student record",
    "Document storage per student",
    "Assessment runner",
    "Articulation Screener with student-facing viewer mode",
    "Use evidence to inform goal planning",
    "Print and display modes for meetings (where available)",
  ];
  return (
    <SectionShell
      id="assessments-documents"
      eyebrow="Assessments & documents"
      title="Keep evaluation context with the student"
      lead="Assessment artifacts, documents, and screeners live with the student record, so the provider running today's session and the admin preparing for the next meeting see the same evaluation context."
      tone="tint"
    >
      <BulletList items={items} columns={2} testId="explore-assessments-bullets" />
    </SectionShell>
  );
}

// ── Section: AI Support ──────────────────────────────────────────

function SectionAiSupport() {
  return (
    <SectionShell
      id="ai-support"
      eyebrow="AI support"
      title="Optional. Advisory. Always reviewed by the team."
      lead="Frame Practice can offer AI support for tasks like goal drafting, evidence review, and planning suggestions where enabled. AI is a drafting and analysis aide — not a decision-maker."
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
        <FeatureCard testId="explore-ai-drafting"
          icon={<Sparkle size={20} weight="duotone" className="text-[#1E3F33]" />}
          title="Drafting assistance"
          body="AI can help draft goal language, suggest evidence groupings, or summarize progress — providers review and edit every output before it lands in the student record." />
        <FeatureCard testId="explore-ai-boundaries"
          icon={<Brain size={20} weight="duotone" className="text-[#1E3F33]" />}
          title="What AI does not do"
          body="AI does not determine eligibility, diagnose disabilities, assign placement, or make final educational decisions. Those remain with qualified school personnel." />
      </div>
    </SectionShell>
  );
}

// ── Section: Privacy / Responsible Use ───────────────────────────

function SectionPrivacyResponsibleUse() {
  return (
    <SectionShell
      id="privacy"
      eyebrow="Privacy & responsible use"
      title="Calm, structured, and team-accountable"
      lead="Frame Practice is built around the realities of school data: invite-only access, role-based permissions, and clear ownership of the work each provider authors. Schools and teams remain responsible for the student information they enter under their own policies."
      tone="tint"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
        <FeatureCard testId="explore-privacy-invite"
          icon={<ShieldCheck size={20} weight="duotone" className="text-[#1E3F33]" />}
          title="Invite-only access"
          body="No public signup. Accounts are issued by an administrator after review." />
        <FeatureCard testId="explore-privacy-roles"
          icon={<Lock size={20} weight="duotone" className="text-[#1E3F33]" />}
          title="Role-based permissions"
          body="Provider, admin, and super-admin scopes — with org and school scoping enforced on every request." />
        <FeatureCard testId="explore-privacy-attribution"
          icon={<Notebook size={20} weight="duotone" className="text-[#1E3F33]" />}
          title="Provider attribution"
          body="Every goal, session, note, and piece of evidence remains attributed to the provider who created it." />
        <FeatureCard testId="explore-privacy-oversight"
          icon={<Users size={20} weight="duotone" className="text-[#1E3F33]" />}
          title="Read-only admin oversight"
          body="Admins see the full picture without overwriting provider-owned documentation." />
        <FeatureCard testId="explore-privacy-terms"
          icon={<ClipboardText size={20} weight="duotone" className="text-[#1E3F33]" />}
          title="First-login terms acceptance"
          body="Every user reviews and accepts the platform terms before reaching the workspace." />
        <FeatureCard testId="explore-privacy-school-ownership"
          icon={<Buildings size={20} weight="duotone" className="text-[#1E3F33]" />}
          title="School data responsibility"
          body="The school or organization remains responsible for what student data is entered and how it's used." />
      </div>
    </SectionShell>
  );
}

// ── Section: Pilot Program ───────────────────────────────────────

function SectionPilotProgram() {
  return (
    <section
      id="pilot"
      data-testid="explore-section-pilot"
      className="bg-[#1E3F33] text-white border-b border-[#173127] scroll-mt-24"
    >
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-14 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-[#8FA998] font-semibold mb-3">
              Product status
            </div>
            <h2 className="text-[26px] sm:text-[32px] lg:text-[36px] font-semibold heading-font leading-[1.15] max-w-2xl">
              Frame Practice is in pilot · Solo providers can start today
            </h2>
            <p className="mt-4 text-[14.5px] sm:text-[15.5px] text-[#D7E6DC] leading-relaxed max-w-2xl">
              We're partnering with a small set of schools, districts, and
              individual providers to shape Frame Practice around real
              school workflows. Solo providers can start a pilot workspace
              directly. Schools, teams, and districts request access so we
              can set up the right admin scope for your org.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
              <Link
                to="/start-pilot"
                data-testid="explore-pilot-start-pilot"
                className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg bg-white text-[#1E3F33] hover:bg-[#F7F5EF] text-[14px] font-medium tracking-tight transition-colors"
              >
                Start a Pilot Workspace
                <ArrowRight size={16} weight="bold" />
              </Link>
              <Link
                to="/request-access"
                data-testid="explore-pilot-request-access"
                className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg border border-[#8FA998] text-white hover:bg-[#173127] text-[14px] font-medium tracking-tight transition-colors"
              >
                Request School/Team Access
              </Link>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                data-testid="explore-pilot-contact-email"
                className="text-[12.5px] text-[#D7E6DC] hover:text-white font-medium underline decoration-[#8FA998]/60 underline-offset-[3px] decoration-1 hover:decoration-white transition-colors"
              >
                Email {CONTACT_EMAIL}
              </a>
            </div>
          </div>
          <div className="rounded-2xl bg-[#173127] border border-[#2C5042] p-6">
            <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#8FA998] font-semibold mb-3">
              Interested in piloting Frame Practice?
            </div>
            <ul className="space-y-2.5 text-[13px] text-[#D7E6DC] leading-relaxed">
              <li className="flex items-start gap-2.5">
                <CheckCircle size={16} weight="duotone" className="text-[#8FA998] mt-0.5 flex-shrink-0" />
                <span>Special education directors and school administrators</span>
              </li>
              <li className="flex items-start gap-2.5">
                <CheckCircle size={16} weight="duotone" className="text-[#8FA998] mt-0.5 flex-shrink-0" />
                <span>SLPs, OTs, PTs, behavior specialists, school psychologists</span>
              </li>
              <li className="flex items-start gap-2.5">
                <CheckCircle size={16} weight="duotone" className="text-[#8FA998] mt-0.5 flex-shrink-0" />
                <span>Special Education Teachers</span>
              </li>
              <li className="flex items-start gap-2.5">
                <CheckCircle size={16} weight="duotone" className="text-[#8FA998] mt-0.5 flex-shrink-0" />
                <span>Small schools and pilot schools evaluating Frame Practice</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Section: Screenshots / UI Preview ────────────────────────────

function SectionScreenshots() {
  return (
    <SectionShell
      id="screenshots"
      eyebrow="A look at the workspace"
      title="Calm, clear surfaces — built around the work"
      lead="Polished preview cards using the live Frame Practice design system. Names and data are neutral demo placeholders only — no real student or provider data appears anywhere on this page."
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
        <PreviewCard label="Today · provider dashboard" testId="explore-screenshot-today">
          <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#8FA998] font-semibold mb-1">
            Wednesday · April 9
          </div>
          <div className="text-[15px] font-semibold text-[#1F2937] heading-font">
            4 sessions today · 2 due soon
          </div>
          <div className="mt-4 space-y-2">
            <TimelineRow time="9:00" label="Student A · Articulation · 30 min" />
            <TimelineRow time="10:15" label="Student B · Language group · 45 min" />
            <TimelineRow time="1:00" label="Student C · Reading fluency · 30 min" upcoming />
            <TimelineRow time="2:30" label="Student D · Self-regulation · 30 min" upcoming />
          </div>
        </PreviewCard>

        <PreviewCard label="Schedule Builder" testId="explore-screenshot-schedule">
          <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#8FA998] font-semibold mb-1">
            This week
          </div>
          <div className="text-[15px] font-semibold text-[#1F2937] heading-font">
            18 service blocks · 92% scheduled
          </div>
          <div className="mt-4 grid grid-cols-5 gap-1.5">
            {Array.from({ length: 25 }).map((_, i) => {
              const filled = i % 4 !== 3;
              return (
                <div
                  key={i}
                  className={`h-7 rounded ${
                    filled
                      ? i % 7 === 0
                        ? "bg-[#9C6B2E]/40 border border-[#9C6B2E]/40"
                        : "bg-[#1E3F33] border border-[#1E3F33]"
                      : "bg-[#F1F2F4] border border-[#EDEAE0]"
                  }`}
                />
              );
            })}
          </div>
          <div className="mt-3 flex items-center gap-4 text-[10.5px] text-[#6B7280]">
            <Legend swatch="#1E3F33" label="Scheduled" />
            <Legend swatch="#9C6B2E" label="Needs make-up" muted />
            <Legend swatch="#F1F2F4" label="Open" outlined />
          </div>
        </PreviewCard>

        <PreviewCard label="Goal progress" testId="explore-screenshot-goal-progress">
          <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#8FA998] font-semibold mb-1">
            Goal · Articulation /s/ in conversation
          </div>
          <div className="text-[15px] font-semibold text-[#1F2937] heading-font">
            72% accuracy · +18% trend
          </div>
          <ProgressChart points={[28, 34, 41, 49, 58, 66, 72]} />
        </PreviewCard>

        <PreviewCard label="Service fidelity · org view" testId="explore-screenshot-fidelity">
          <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#8FA998] font-semibold mb-1">
            Provider · Provider 1 (SLP)
          </div>
          <div className="text-[15px] font-semibold text-[#1F2937] heading-font">
            14 students · 1 needs make-up
          </div>
          <div className="mt-4 space-y-2.5">
            <FidelityRow label="Speech · direct" delivered={48} expected={50} />
            <FidelityRow label="Speech · consult" delivered={12} expected={20} />
          </div>
        </PreviewCard>
      </div>
    </SectionShell>
  );
}

function TimelineRow({ time, label, upcoming }) {
  return (
    <div className="flex items-start gap-3 py-1.5 border-b border-[#F1F2F4] last:border-0">
      <div
        className={`text-[11px] font-semibold heading-font w-12 flex-shrink-0 ${upcoming ? "text-[#9C6B2E]" : "text-[#1E3F33]"}`}
      >
        {time}
      </div>
      <div className="text-[12.5px] text-[#1F2937] leading-snug">{label}</div>
    </div>
  );
}

function Legend({ swatch, label, muted, outlined }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-block w-3 h-3 rounded"
        style={{
          backgroundColor: muted ? `${swatch}66` : outlined ? "transparent" : swatch,
          border: outlined ? `1px solid #EDEAE0` : "none",
        }}
      />
      {label}
    </span>
  );
}

function ProgressChart({ points }) {
  const max = Math.max(...points, 100);
  const w = 100;
  const h = 40;
  const stepX = w / (points.length - 1);
  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${(i * stepX).toFixed(2)},${(h - (p / max) * h).toFixed(2)}`)
    .join(" ");
  return (
    <div className="mt-3">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-20 overflow-visible">
        <path d={`${path} L${w},${h} L0,${h} Z`} fill="#E8F0EB" />
        <path d={path} stroke="#1E3F33" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        {points.map((p, i) => (
          <circle key={i} cx={i * stepX} cy={h - (p / max) * h} r={1.6} fill="#1E3F33" />
        ))}
      </svg>
      <div className="mt-2 flex items-center justify-between text-[10.5px] text-[#6B7280]">
        <span>Week 1</span>
        <span>Week {points.length}</span>
      </div>
    </div>
  );
}

// ── Footer ───────────────────────────────────────────────────────

function PublicFooter() {
  return (
    <footer
      data-testid="explore-footer"
      className="bg-[#F7F5EF] border-t border-[#EDEAE0]"
    >
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-8 h-8 rounded-lg bg-[#1E3F33] flex items-center justify-center">
                <span className="text-white text-xs font-bold heading-font">F</span>
              </div>
              <div className="leading-tight">
                <div className="text-[14px] font-bold text-[#1E3F33] heading-font tracking-tight">
                  Frame<span className="text-[#8FA998] font-medium ml-0.5">Practice</span>
                </div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-[#8FA998]">
                  Part of FrameEd
                </div>
              </div>
            </div>
            <p className="text-[11.5px] text-[#6B7280] max-w-md leading-relaxed">
              Provider workspace for school-based teams. Solo providers can start a pilot today.
            </p>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-[12px]">
            <Link to="/start-pilot" data-testid="explore-footer-start-pilot"
              className="text-[#1E3F33] font-semibold hover:text-[#173127] underline decoration-[#8FA998]/60 underline-offset-[3px] decoration-1 hover:decoration-[#1E3F33] transition-colors">
              Start pilot
            </Link>
            <Link to="/login" data-testid="explore-footer-signin"
              className="text-[#1E3F33] font-medium hover:text-[#173127] underline decoration-[#8FA998]/60 underline-offset-[3px] decoration-1 hover:decoration-[#1E3F33] transition-colors">
              Sign in
            </Link>
            <Link to="/request-access" data-testid="explore-footer-request-access"
              className="text-[#1E3F33] font-medium hover:text-[#173127] underline decoration-[#8FA998]/60 underline-offset-[3px] decoration-1 hover:decoration-[#1E3F33] transition-colors">
              Request access
            </Link>
            <a href={`mailto:${CONTACT_EMAIL}`} data-testid="explore-footer-contact"
              className="text-[#1E3F33] font-medium hover:text-[#173127] underline decoration-[#8FA998]/60 underline-offset-[3px] decoration-1 hover:decoration-[#1E3F33] transition-colors">
              {CONTACT_EMAIL}
            </a>
          </div>
        </div>
        <div className="mt-7 pt-5 border-t border-[#EDEAE0] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-[10.5px] text-[#9AA0A6]">
          <span>© {new Date().getFullYear()} FrameEd · Frame Practice. All rights reserved.</span>
          <span>Pilot · solo start · school/team invite-only</span>
        </div>
      </div>
    </footer>
  );
}
